import { TemplateResponse } from 'services/types/message';

import {
  EditProjectBody,
  Project,
  MetricsObject,
  ProjectDraft,
  TemplateCreate,
  MondayBoardStatusResponse,
  CreateBoardResponse,
} from '../types/project';
import { ProjectCloneRequest, ProjectCloneResponse } from '../types/projectClone';
import baseApi from './baseApi';

export interface FileUploadResult {
  success: boolean;
  fileName: string;
  bucketPath: string;
  originalUrl?: string;
  error?: string;
}

export interface FileUploadResponse {
  results: FileUploadResult[];
  successful: number;
  failed: number;
  errors?: Array<{ fileName: string; error?: string }>;
}

export interface TemplateCreationFileResponse {
  url: string;
  headerHandle: string;
}

const projectApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProject: build.query({
      query: () => ({
        url: '/user/project',
        method: 'GET',
      }),
      transformResponse: (response: Project | null): Project | null => response,
    }),
    adminGetProject: build.query({
      query: (projectId: number) => ({
        url: `/admin/projects/${projectId}`,
        method: 'GET',
      }),
      transformResponse: (response: Project | null): Project | null => response,
    }),
    getProjects: build.query({
      query: () => ({
        url: `/admin/projects`,
        method: 'GET',
      }),
      transformResponse: (response: Project[]): Project[] =>
        response
          .map((project) => ({
            project,
            sortValue1: project.masterProject
              ? `${project.masterProject.name} - ${project.masterProject.id}`
              : `${project.name} - ${project.id}`,
            sortValue2: project.masterProjectId ? `${project.name} - ${project.id}` : '',
          }))
          .sort((a, b) => {
            const primary = a.sortValue1.localeCompare(b.sortValue1, undefined, { sensitivity: 'base' });
            if (primary !== 0) return primary;
            return a.sortValue2.localeCompare(b.sortValue2, undefined, { sensitivity: 'base' });
          })
          .map((project) => project.project),
    }),
    getProjectMetrics: build.query({
      query: (params: {
        fromDate: string;
        toDate: string;
        specificProjectId?: number | null;
        specificSubProjectId?: number | null;
      }) => ({
        url: 'user/project/metrics',
        method: 'GET',
        params,
      }),
      transformResponse: (response: MetricsObject): MetricsObject => response, // Should I specify the type here?
    }),
    adminGetProjectWithId: build.mutation({
      query: (id: number) => ({
        url: `/admin/projects/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Project): Project => response,
    }),
    createProject: build.mutation({
      query: (body: ProjectDraft) => ({
        url: '/admin/projects',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Project): Project => response,
    }),
    editProject: build.mutation({
      query: (props: { id: number; body: EditProjectBody }) => ({
        url: `/admin/projects/${props.id}`,
        method: 'PATCH',
        body: props.body,
      }),
      transformResponse: (response: Project): Project => response,
    }),
    adminGetProjectAnalytics: build.mutation({
      query: (id: number) => ({
        url: `/admin/projects/${id}/analytics`,
        method: 'GET',
      }),
      transformResponse: (response: {
        totalMessagesCost: number;
        totalFloidRequestsCost: number;
        totalCost: number;
        totalClients: number;
      }): { cost: number; clientsCount: number } => ({ cost: response.totalCost, clientsCount: response.totalClients }),
    }),
    deleteProjectImage: build.mutation({
      query: (props: { projectId: number; fileName: string }) => ({
        // replace spaces in file name with %20
        url: `/admin/projects/${props.projectId}/images/${props.fileName.replace(/ /g, '%20')}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadProjectImages: build.mutation<FileUploadResponse, { projectId: number; files: File[] }>({
      query: (props: { projectId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/projects/${props.projectId}/images`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: FileUploadResponse) => response,
    }),
    deleteProjectQuote: build.mutation({
      query: (props: { projectId: number; fileName: string }) => ({
        url: `/admin/projects/${props.projectId}/quotes/${props.fileName}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadProjectQuotes: build.mutation<FileUploadResponse, { projectId: number; files: File[] }>({
      query: (props: { projectId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/projects/${props.projectId}/quotes`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: FileUploadResponse) => response,
    }),
    deleteProjectFile: build.mutation({
      query: (props: { projectId: number; fileName: string }) => ({
        url: `/admin/projects/${props.projectId}/files/${props.fileName.replace(/ /g, '%20')}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    uploadProjectFiles: build.mutation<FileUploadResponse, { projectId: number; files: File[] }>({
      query: (props: { projectId: number; files: File[] }) => {
        const formData = new FormData();
        props.files.forEach((file) => {
          formData.append('file', file);
        });
        return {
          url: `/admin/projects/${props.projectId}/files`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: FileUploadResponse) => response,
    }),
    createProjectTemplate: build.mutation<TemplateResponse, { projectId: number; template: TemplateCreate }>({
      query: (props: { projectId: number; template: TemplateCreate }) => ({
        url: `project/${props.projectId}/template`,
        method: 'POST',
        body: props.template,
      }),
      transformResponse: (response: TemplateResponse) => response,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TemplateCatalog' as const, id: projectId }],
    }),
    uploadTemplateFile: build.mutation({
      query: (props: { projectId: number; file: File }) => {
        const formData = new FormData();
        formData.append('file', props.file);
        return {
          url: `project/${props.projectId}/template-file`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: { url: string }) => response,
    }),
    uploadTemplateCreationFile: build.mutation<
      TemplateCreationFileResponse,
      { projectId: number; file: File; format: 'IMAGE' | 'DOCUMENT' }
    >({
      query: (props) => {
        const formData = new FormData();
        formData.append('file', props.file);
        formData.append('format', props.format);
        return {
          url: `project/${props.projectId}/template-creation-file`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: TemplateCreationFileResponse) => response,
    }),
    getProjectTemplates: build.query({
      query: (projectId: number) => ({
        url: `/project/${projectId}/templates`,
        method: 'GET',
      }),
      transformResponse: (response: TemplateResponse[]) => response,
    }),
    getMondayBoardStatus: build.query<MondayBoardStatusResponse, number>({
      query: (projectId: number) => ({
        url: `/admin/projects/${projectId}/monday/status`,
        method: 'GET',
      }),
      transformResponse: (response: MondayBoardStatusResponse): MondayBoardStatusResponse => response,
    }),
    createMondayBoard: build.mutation<CreateBoardResponse, { projectId: number; boardType: 'initial' | 'successive' }>({
      query: ({ projectId, boardType }) => ({
        url: `/admin/projects/${projectId}/monday/create-board/${boardType}`,
        method: 'POST',
      }),
      transformResponse: (response: CreateBoardResponse): CreateBoardResponse => response,
    }),
    getPubsubEvents: build.query<{ pubsubEvents: string[] }, number>({
      query: (projectId) => ({
        url: `/admin/projects/events/${projectId}`,
        method: 'GET',
      }),
      transformResponse: (response: { pubsubEvents: string[] }): { pubsubEvents: string[] } => response,
    }),
    addPubsubEvent: build.mutation<
      { pubsubEvents: string[] },
      { projectId: number; event: 'ai_evals' | 'notifications' }
    >({
      query: ({ projectId, event }) => ({
        url: `/admin/projects/events/${projectId}`,
        method: 'PATCH',
        body: {
          event,
          action: 'add',
        },
      }),
      transformResponse: (response: { pubsubEvents: string[] }): { pubsubEvents: string[] } => response,
    }),
    deleteProjectTemplate: build.mutation({
      query: (props: { projectId: number; templateName: string }) => ({
        url: `project/${props.projectId}/template/${props.templateName}`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => response,
    }),
    cloneProject: build.mutation<ProjectCloneResponse, ProjectCloneRequest>({
      query: (body) => ({
        url: 'admin/projectClone/clone',
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProjectQuery,
  useGetProjectsQuery,
  useGetProjectMetricsQuery,
  useAdminGetProjectQuery,
  useAdminGetProjectWithIdMutation,
  useCreateProjectMutation,
  useEditProjectMutation,
  useAdminGetProjectAnalyticsMutation,
  useDeleteProjectImageMutation,
  useUploadProjectImagesMutation,
  useDeleteProjectQuoteMutation,
  useUploadProjectQuotesMutation,
  useDeleteProjectFileMutation,
  useUploadProjectFilesMutation,
  useCreateProjectTemplateMutation,
  useUploadTemplateFileMutation,
  useUploadTemplateCreationFileMutation,
  useGetProjectTemplatesQuery,
  useGetMondayBoardStatusQuery,
  useCreateMondayBoardMutation,
  useGetPubsubEventsQuery,
  useAddPubsubEventMutation,
  useDeleteProjectTemplateMutation,
  useCloneProjectMutation,
} = projectApi;
